import { Loader, SectionMessage, showToast } from "@certn/cascadia-react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useClientPortalAPI, useSubmit } from "@certn/api";
import { Formik } from "formik";
import {
  CustomQuestionnaireTemplateInput,
  validationSchema,
} from "./custom-questionnaire/utils";
import { TemplateLayout } from "./TemplateLayout";
import { CustomQuestionnaireTemplateForm } from "./custom-questionnaire/CustomQuestionnaireTemplateForm";

export const CustomTemplateEditPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();

  const goBack = () => navigate("/settings/custom-questionnaire-templates");

  const {
    data: template,
    isLoading,
    error,
  } = useClientPortalAPI(
    `/api/internal/client-portal/custom-questionnaire-templates/${id}/`
  );

  const { submit } = useSubmit(
    `/api/internal/client-portal/custom-questionnaire-templates/${id}/update/`,
    {
      throwErrors: true,
      showToastOnError: true,
      onSuccess: () => {
        showToast(t("Template updated successfully!"), { type: "success" });
        goBack();
      },
    }
  );

  const updateTemplate = async (name: string) => {
    await submit({ name });
  };

  if (isLoading) {
    return <Loader overlay />;
  }

  if (error) {
    return (
      <SectionMessage
        type="danger"
        sectionTitle={t("Unable to retrieve custom template.")}
        icon="error"
      >
        {error.message}
      </SectionMessage>
    );
  }

  const orderedQuestions = [...(template?.questions || [])].sort(
    (a, b) => a.display_priority - b.display_priority
  );

  return (
    <Formik<CustomQuestionnaireTemplateInput>
      enableReinitialize
      validateOnMount
      initialValues={{
        name: template?.name || "",
        questions: orderedQuestions.map((question) => ({
          id: question.id,
          external_name: question.external_name,
          description: question.description || undefined,
          data_type: question.data_type,
          is_optional: question.is_optional,
          display_priority: question.display_priority,
          display_options: question.display_options,
          selectable_options:
            question.selectable_options &&
            Array.isArray(question.selectable_options)
              ? question.selectable_options
              : [],
          display_conditions: question.display_conditions.map((c) => ({
            parent_question_id: c.parent_question.id,
            condition_value: c.condition_value,
          })),
        })),
      }}
      validationSchema={validationSchema}
      onSubmit={async (values) => {
        await updateTemplate(values.name);
      }}
    >
      {({
        values,
        handleSubmit,
        isSubmitting,
        isValid,
        setValues,
        dirty,
        setFieldValue,
      }) => {
        return (
          <TemplateLayout
            hideCancelModal
            type="update"
            title={t("Custom questionnaire template")}
            onComplete={handleSubmit}
            submitting={isSubmitting}
            onCancel={goBack}
            disableCompletion={!isValid || !dirty}
          >
            <CustomQuestionnaireTemplateForm
              type="update"
              values={values}
              setValues={setValues}
              setFieldValue={setFieldValue}
            />
          </TemplateLayout>
        );
      }}
    </Formik>
  );
};
